import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { cn } from "../../lib/cn";

export type ToastTone = "default" | "error";

interface Toast {
	id: number;
	message: string;
	tone: ToastTone;
}

interface ToastApi {
	push: (message: string, tone?: ToastTone) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DISMISS_AFTER_MS = 3500;

export const useToast = (): ToastApi => {
	const api = useContext(ToastContext);
	if (!api) throw new Error("useToast must be used inside ToastProvider");
	return api;
};

export const ToastProvider = ({ children }: { children: ReactNode }) => {
	const [toasts, setToasts] = useState<Toast[]>([]);
	const nextId = useRef(0);

	const push = useCallback((message: string, tone: ToastTone = "default") => {
		nextId.current += 1;
		const id = nextId.current;
		setToasts((current) => [...current, { id, message, tone }]);
		setTimeout(
			() => setToasts((current) => current.filter((toast) => toast.id !== id)),
			DISMISS_AFTER_MS,
		);
	}, []);

	return (
		<ToastContext.Provider value={{ push }}>
			{children}
			<div className="pointer-events-none fixed right-4 bottom-4 z-50 flex flex-col gap-2">
				{toasts.map((toast) => (
					<div
						key={toast.id}
						role="status"
						className={cn(
							"pointer-events-auto rounded-md border bg-surface px-3 py-2 text-sm shadow-lg",
							toast.tone === "error" ? "border-ink text-ink" : "border-line-strong text-ink-muted",
						)}
					>
						{toast.message}
					</div>
				))}
			</div>
		</ToastContext.Provider>
	);
};
